import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, Store, ShoppingCart, TrendingUp, CheckCircle, Truck, Shield, Star, Mail, ArrowRight, Code, Users, TestTube2 as TestTube, ExternalLink } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AMAZON_STORE_URL = "https://www.amazon.com/stores/TRENDO/page/3D435283-278E-44B3-83C7-66C5C0BCC5C0?lp_asin=B0DRT7VDLX&ref_=ast_bln&store_ref=bl_ast_dp_brandLogo_sto";

const iconMap = {
  ShoppingBag, Store, ShoppingCart, TrendingUp, CheckCircle, Truck, Shield, Star, Code, Users, TestTube
};

export const HomePage = () => {
  const [platforms, setPlatforms] = useState([]);
  const [about, setAbout] = useState({ story_paragraphs: [], values: [] });
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [platformsRes, aboutRes, testimonialsRes, faqsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/about`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/faqs`)
      ]);
      setPlatforms(platformsRes.data);
      setAbout(aboutRes.data);
      setTestimonials(testimonialsRes.data);
      setFaqs(faqsRes.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        project_type: 'inquiry',
        budget_range: 'n/a',
        timeline: 'n/a'
      });
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="hero-section" id="home">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-4xl mx-auto fade-in">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 mb-6" data-testid="hero-heading">
              Snappadeal<br />E-Commerce + Software Consulting
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed" data-testid="hero-subheading">
              Shop our <strong>TRENDO</strong> brand on Amazon or leverage our expertise in software development and testing consulting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={AMAZON_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#FF9900] text-slate-900 hover:bg-[#FFB84D] rounded-full px-8 py-4 font-bold transition-all duration-300 hover:shadow-lg hover:-translate-y-1 text-base"
                data-testid="hero-amazon-button"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48-.256.19-.6.41-1.006.654-1.244.748-2.143 1.18-2.693 1.295-.547.114-1.35.244-2.405.394-.883.1-1.793.15-2.73.15-2.097 0-4.17-.34-6.217-1.022-2.293-.763-4.33-1.86-6.107-3.296-.11-.1-.135-.2-.075-.298zm6.965-2.485c-.68-.058-1.236-.232-1.67-.53-.434-.297-.77-.734-1.012-1.31-.242-.576-.363-1.26-.363-2.049 0-1.156.33-2.136.988-2.941.658-.805 1.524-1.21 2.594-1.21.32 0 .613.03.876.092.264.06.5.15.707.27.208.12.377.253.506.4.13.147.23.304.3.47l.157-.41c.07-.17.138-.32.2-.45.06-.13.148-.27.268-.42.12-.15.26-.27.418-.36.16-.09.366-.14.62-.14.253 0 .47.05.65.15.18.1.313.248.4.443.086.196.13.4.13.615v6.52c0 1.226-.34 2.175-1.015 2.846-.676.672-1.62 1.008-2.836 1.008-.472 0-.94-.05-1.397-.15-.46-.1-.85-.26-1.178-.48-.327-.22-.58-.49-.756-.81-.176-.32-.265-.69-.265-1.11 0-.32.067-.6.2-.84.134-.24.317-.43.55-.57.232-.14.5-.24.8-.31.302-.07.62-.105.957-.105h1.82v-.39c0-.503-.1-.886-.296-1.148-.198-.263-.515-.394-.953-.394-.316 0-.575.06-.774.18-.2.12-.35.3-.453.54l-1.678-.336c.177-.612.515-1.087 1.014-1.426.5-.34 1.12-.508 1.862-.508.82 0 1.468.195 1.945.586.476.39.714.943.714 1.66v3.82c0 .25.053.427.16.53.107.105.275.157.504.157.123 0 .263-.02.42-.06v1.21c-.27.08-.556.12-.856.12-.59 0-1.044-.143-1.364-.43-.32-.285-.48-.68-.48-1.185v-.15c-.187.432-.48.775-.88 1.03-.4.253-.88.38-1.44.38zm1.86-2.97c-.328 0-.583.07-.765.21-.182.14-.273.34-.273.6 0 .26.088.46.263.6.176.14.42.21.73.21.31 0 .585-.073.823-.22.237-.147.42-.343.548-.59.128-.246.192-.513.192-.8v-.01h-1.52zm5.246 2.07l-2.096-6.76h1.844l1.26 4.62 1.32-4.62h1.74l1.3 4.62 1.278-4.62h1.826l-2.124 6.76h-1.814l-1.37-4.52-1.35 4.52h-1.814z"/>
                </svg>
                Shop TRENDO on Amazon
                <ExternalLink size={18} />
              </a>
              <a
                href="#services"
                className="bg-[#0F172A] text-white hover:bg-[#0F172A]/90 rounded-full px-8 py-4 font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1 text-base text-center"
                data-testid="hero-cta-primary"
              >
                Our Services
              </a>
              <a
                href="#contact"
                className="bg-white text-[#0F172A] border border-slate-200 hover:border-slate-400 hover:bg-slate-50 rounded-full px-8 py-4 font-medium transition-all duration-300 text-base text-center"
                data-testid="hero-cta-secondary"
              >
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-section" id="about">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">About Snappadeal</h2>
            <div className="max-w-3xl mx-auto space-y-4 text-lg text-slate-600">
              {about.story_paragraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          {/* Values */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {about.values.map((value, index) => {
              const IconComponent = iconMap[value.icon] || ShoppingBag;
              return (
                <div key={index} className="text-center" data-testid={`value-${index}`}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#10B981]/10 rounded-full mb-4">
                    <IconComponent size={32} className="text-[#10B981]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="services-section" id="services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">What We Do</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From Amazon e-commerce to software consulting - we deliver excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => {
              const IconComponent = iconMap[platform.icon] || ShoppingBag;
              return (
                <div
                  key={platform.id}
                  className="bg-white border border-slate-100 p-8 rounded-2xl shadow-sm hover:shadow-xl hover:border-[#10B981] transition-all duration-300 group"
                  data-testid={`service-${index}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="text-[#10B981] group-hover:scale-110 transition-transform duration-300">
                      <IconComponent size={40} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">{platform.title}</h3>
                      <span className="inline-block bg-[#10B981]/10 text-[#10B981] px-3 py-1 rounded-full text-sm font-medium">
                        {platform.timeline}
                      </span>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">{platform.description}</p>
                  <div className="space-y-2">
                    {platform.outcomes.slice(0, 5).map((outcome, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={18} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600 text-sm">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-white" data-testid="testimonials-section" id="testimonials">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">What Customers Say</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real reviews from real customers across all platforms
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-slate-50 border border-slate-200 p-8 rounded-2xl relative"
                data-testid={`testimonial-${index}`}
              >
                <div className="text-6xl text-[#10B981] opacity-20 absolute top-4 left-4">"</div>
                <p className="text-slate-700 mb-6 relative z-10">{testimonial.content}</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <div className="font-heading font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="faq-section" id="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-slate-600">Everything you need to know</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={`item-${index}`}
                className="bg-white border border-slate-200 rounded-lg px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="font-heading font-medium text-left hover:text-[#10B981]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-section" id="contact">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">Get in Touch</h2>
              <p className="text-lg text-slate-600 mb-8">
                Have questions about our products or want to discuss bulk orders? We're here to help!
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <Mail size={24} className="text-[#10B981]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@snappadeal.com" className="text-slate-600 hover:text-[#10B981]">
                      contact@snappadeal.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    required
                    rows={5}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-full h-12 text-lg font-bold"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
